import { baseSupabase } from '@/integrations/supabase/client';

export type LaunchFlagKey =
  | 'enable_trust_score_v2'
  | 'enable_chat_v2'
  | 'enable_paywall_ab_test'
  | 'enable_discover_filters'
  | 'maintenance_mode'
  | 'enable_verified_badge';

type FlagSourceTable = 'app_config' | 'remote_config' | 'defaults';

export interface FlagSnapshot {
  flags: Record<LaunchFlagKey, boolean>;
  sourceTable: FlagSourceTable;
  lastUpdatedAt: string;
  fetchedAt: string;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // P2: 5 minutes (was 60s)
const REQUESTED_KEYS = [
  'enable_trust_score_v2',
  'enable_chat_v2',
  'enable_paywall_ab_test',
  'enable_discover_filters',
  'maintenance_mode',
  'enable_verified_badge',
] as const satisfies readonly LaunchFlagKey[];

const DEFAULT_FLAGS: Record<LaunchFlagKey, boolean> = {
  enable_trust_score_v2: false,
  enable_chat_v2: false,
  enable_paywall_ab_test: false,
  enable_discover_filters: false,
  maintenance_mode: false,
  enable_verified_badge: false,
};

function nowIso(): string {
  return new Date().toISOString();
}

function toBoolean(raw: unknown): boolean {
  if (raw == null) return false;
  const normalized = String(raw).trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
}

class FlagService {
  private cache: FlagSnapshot = {
    flags: { ...DEFAULT_FLAGS },
    sourceTable: 'defaults',
    lastUpdatedAt: nowIso(),
    fetchedAt: '1970-01-01T00:00:00.000Z',
  };

  private inFlight: Promise<FlagSnapshot> | null = null;
  private unavailableSources = new Set<Exclude<FlagSourceTable, 'defaults'>>();
  private warnedMissingSource = new Set<Exclude<FlagSourceTable, 'defaults'>>();

  getCached(): FlagSnapshot {
    return this.cache;
  }

  isEnabled(key: LaunchFlagKey): boolean {
    return this.cache.flags[key] ?? false;
  }

  async refresh(force = false): Promise<FlagSnapshot> {
    const ageMs = Date.now() - Date.parse(this.cache.fetchedAt);
    if (!force && ageMs < CACHE_TTL_MS) {
      return this.cache;
    }

    if (this.inFlight) return this.inFlight;

    this.inFlight = (async () => {
      try {
        const appConfigSnapshot = await this.fetchFromAppConfig();
        if (appConfigSnapshot) {
          this.cache = appConfigSnapshot;
          return this.cache;
        }

        const remoteConfigSnapshot = await this.fetchFromRemoteConfig();
        if (remoteConfigSnapshot) {
          this.cache = remoteConfigSnapshot;
          return this.cache;
        }

        this.cache = this.buildDefaultSnapshot();
        return this.cache;
      } catch {
        this.cache = this.buildDefaultSnapshot();
        return this.cache;
      } finally {
        this.inFlight = null;
      }
    })();

    return this.inFlight;
  }

  private buildDefaultSnapshot(): FlagSnapshot {
    return {
      flags: { ...DEFAULT_FLAGS },
      sourceTable: 'defaults',
      lastUpdatedAt: nowIso(),
      fetchedAt: nowIso(),
    };
  }

  private applyRows(
    sourceTable: Exclude<FlagSourceTable, 'defaults'>,
    rows: Array<{ key: unknown; value?: unknown; enabled?: unknown; updated_at?: unknown }>,
  ): FlagSnapshot {
    const nextFlags: Record<LaunchFlagKey, boolean> = { ...DEFAULT_FLAGS };
    let maxUpdatedAt = this.cache.lastUpdatedAt;

    for (const row of rows) {
      const key = String(row.key) as LaunchFlagKey;
      if (!(key in DEFAULT_FLAGS)) continue;
      const rawValue = sourceTable === 'app_config' ? row.value : row.enabled;
      nextFlags[key] = typeof rawValue === 'boolean' ? rawValue : toBoolean(rawValue);
      if (typeof row.updated_at === 'string' && row.updated_at > maxUpdatedAt) {
        maxUpdatedAt = row.updated_at;
      }
    }

    return {
      flags: nextFlags,
      sourceTable,
      lastUpdatedAt: maxUpdatedAt,
      fetchedAt: nowIso(),
    };
  }

  private async fetchFromAppConfig(): Promise<FlagSnapshot | null> {
    if (this.unavailableSources.has('app_config')) {
      return null;
    }

    const { data, error } = await baseSupabase
      .from('app_config')
      .select('key,value,updated_at')
      .in('key', REQUESTED_KEYS);

    if (error) {
      if (this.isMissingRelationError(error)) {
        this.unavailableSources.add('app_config');
        this.warnMissingSourceOnce('app_config', error);
      }
      return null;
    }

    return this.applyRows('app_config', (data ?? []) as Array<{ key: unknown; value?: unknown; updated_at?: unknown }>);
  }

  private async fetchFromRemoteConfig(): Promise<FlagSnapshot | null> {
    if (this.unavailableSources.has('remote_config')) {
      return null;
    }

    const { data, error } = await baseSupabase
      .from('remote_config')
      .select('key,enabled,updated_at')
      .in('key', REQUESTED_KEYS);

    if (error) {
      if (this.isMissingRelationError(error)) {
        this.unavailableSources.add('remote_config');
        this.warnMissingSourceOnce('remote_config', error);
      }
      return null;
    }

    return this.applyRows('remote_config', (data ?? []) as Array<{ key: unknown; enabled?: unknown; updated_at?: unknown }>);
  }

  private isMissingRelationError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const postgrest = error as { code?: string; status?: number; message?: string; details?: string };
    const message = `${postgrest.message ?? ''} ${postgrest.details ?? ''}`.toLowerCase();
    return (
      postgrest.status === 404 ||
      postgrest.code === 'PGRST205' ||
      postgrest.code === '42P01' ||
      message.includes('could not find the table') ||
      message.includes('relation') && message.includes('does not exist')
    );
  }

  private warnMissingSourceOnce(source: Exclude<FlagSourceTable, 'defaults'>, error: unknown): void {
    if (!import.meta.env.DEV || this.warnedMissingSource.has(source)) return;
    this.warnedMissingSource.add(source);
    console.warn(`[flagService] Optional flag source "${source}" not available; using fallback/default flags.`, error);
  }
}

export const flagService = new FlagService();
